import apiClient from './client';
import type { PaginatedResponse } from '../types';

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

export interface ProjectReportAuthor {
  id: string;
  full_name: string;
}

export interface ProjectReport {
  id: string;
  project_id: string;
  title: string;
  author_names: string;
  authors: ProjectReportAuthor[];
  customer_id: string | null;
  customer_name: string | null;
  department: string | null;
  competence_field_ids: string[];
  competence_field_names: string[];
  contract_type: string | null;
  sector_ids: string[];
  sector_names: string[];
  technology_ids: string[];
  technology_names: string[];
  skill_ids: string[];
  skill_names: string[];
  content: string | null;
  challenges: string | null;
  next_steps: string | null;
  potentials: string | null;
  lessons_learned: string | null;
  project_start: string | null;
  project_end: string | null;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED';
  has_pending_changes: boolean;
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: string;
  title: string;
}

export interface Sector {
  id: string;
  title: string;
}

export interface Technology {
  id: string;
  title: string;
}

export interface CompetenceField {
  id: string;
  title: string;
}

export interface Skill {
  id: string;
  name: string;
  category: string | null;
}

export interface ProjectReportListParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
}

export interface ProjectReportPayload {
  project_id: string;
  title: string;
  author_ids?: string[];
  customer_id?: string | null;
  department?: string | null;
  competence_field_ids?: string[];
  contract_type?: string | null;
  sector_ids?: string[];
  technology_ids?: string[];
  skill_ids?: string[];
  content?: string | null;
  challenges?: string | null;
  next_steps?: string | null;
  potentials?: string | null;
  lessons_learned?: string | null;
  project_start?: string | null;
  project_end?: string | null;
}

// -------------------------------------------------------------------------
// API functions
// -------------------------------------------------------------------------

const BASE = '/project-reports';

export async function fetchProjectReports(
  params: ProjectReportListParams = {},
): Promise<PaginatedResponse<ProjectReport>> {
  const springParams: Record<string, unknown> = {
    page: (params.page ?? 1) - 1,
    size: params.page_size ?? 20,
    ...(params.search ? { search: params.search } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.sort_by
      ? {
          sort: `${params.sort_by.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase())},${params.order ?? 'desc'}`,
        }
      : {}),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await apiClient.get<any>(BASE, { params: springParams });

  return {
    items: data.content ?? [],
    total: data.total_elements ?? 0,
    page: (data.number ?? 0) + 1,
    page_size: data.size ?? 20,
    total_pages: data.total_pages ?? 0,
  };
}

export async function fetchProjectReport(id: string): Promise<ProjectReport> {
  const { data } = await apiClient.get<ProjectReport>(`${BASE}/${id}`);
  return data;
}

export async function createProjectReport(
  payload: ProjectReportPayload,
): Promise<ProjectReport> {
  const { data } = await apiClient.post<ProjectReport>(BASE, payload);
  return data;
}

export async function updateProjectReport(
  id: string,
  payload: ProjectReportPayload,
): Promise<ProjectReport> {
  const { data } = await apiClient.put<ProjectReport>(`${BASE}/${id}`, payload);
  return data;
}

export async function deleteProjectReport(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`);
}

export async function submitForApproval(id: string): Promise<ProjectReport> {
  const { data } = await apiClient.post<ProjectReport>(
    `${BASE}/${id}/submit-for-approval`,
  );
  return data;
}

export async function approveReport(id: string): Promise<ProjectReport> {
  const { data } = await apiClient.post<ProjectReport>(`${BASE}/${id}/approve`);
  return data;
}

export async function rejectReport(id: string): Promise<ProjectReport> {
  const { data } = await apiClient.post<ProjectReport>(`${BASE}/${id}/reject`);
  return data;
}

// -------------------------------------------------------------------------
// Reference data (customers, sectors, technologies)
// -------------------------------------------------------------------------

export async function fetchCustomers(): Promise<Customer[]> {
  const { data } = await apiClient.get<Customer[]>(`${BASE}/customers`);
  return data;
}

export async function createCustomer(title: string): Promise<Customer> {
  const { data } = await apiClient.post<Customer>(`${BASE}/customers`, { title });
  return data;
}

export async function fetchSectors(): Promise<Sector[]> {
  const { data } = await apiClient.get<Sector[]>(`${BASE}/sectors`);
  return data;
}

export async function createSector(title: string): Promise<Sector> {
  const { data } = await apiClient.post<Sector>(`${BASE}/sectors`, { title });
  return data;
}

export async function fetchTechnologies(): Promise<Technology[]> {
  const { data } = await apiClient.get<Technology[]>(`${BASE}/technologies`);
  return data;
}

export async function createTechnology(title: string): Promise<Technology> {
  const { data } = await apiClient.post<Technology>(`${BASE}/technologies`, {
    title,
  });
  return data;
}

export async function fetchCompetenceFields(): Promise<CompetenceField[]> {
  const { data } = await apiClient.get<CompetenceField[]>(`${BASE}/competence-fields`);
  return data;
}

export async function createCompetenceField(title: string): Promise<CompetenceField> {
  const { data } = await apiClient.post<CompetenceField>(`${BASE}/competence-fields`, {
    title,
  });
  return data;
}

export async function fetchSkills(): Promise<Skill[]> {
  const { data } = await apiClient.get<Skill[]>(`${BASE}/skills`);
  return data;
}

export async function searchSkills(query: string): Promise<Skill[]> {
  const { data } = await apiClient.get<Skill[]>(`${BASE}/skills/search`, {
    params: { q: query },
  });
  return data;
}
